// Adapted from Chakra's Modal theme to use our color palate,
// but always isCentered and scrollBehavior=outside
import type { SystemStyleObject } from '@chakra-ui/react-v2';

import { type StyleObjectMulti } from '@frontend/legacy-components/src/theme/styleConfigHelpers';

import { OVERLINE_STYLES } from '../drawer/const';
import {
  MODAL_HEADER_ACTIONS_ATTRIBUTE,
  MODAL_HEADER_ACTIONS_WIDTH_VAR,
} from './const';
import { MODAL_PARTS } from './modalParts';
import type { ModalPart, ModalSize, ModalStyleConfigActual } from './types';

const BASE_STYLE = {
  overlay: {
    bg: 'overlay.modal',
    zIndex: 'modal',
  },
  dialogContainer: {
    display: 'flex',
    zIndex: 'modal',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'auto',
    padding: { base: '0', tablet: 4, desktop: 8 },
  },
  dialog: {
    borderRadius: 'lg',
    bg: 'page.surface',
    color: 'inherit',
    my: '3.75rem',
    zIndex: 'modal',
    boxShadow: 'popover',
    // Reserve room for the absolutely positioned close button (and move any
    // ModalHeaderActions slot to sit to its left).
    '&:has(.chakra-modal__close-btn) .chakra-modal__header': {
      pe: `calc(var(--chakra-space-9) + var(${MODAL_HEADER_ACTIONS_WIDTH_VAR}, 0px))`,
    },
    [`&:has(.chakra-modal__close-btn) [${MODAL_HEADER_ACTIONS_ATTRIBUTE}]`]: {
      insetEnd: 7,
    },
  },
  header: {
    px: 2,
    pe: `calc(var(--chakra-space-2) + var(${MODAL_HEADER_ACTIONS_WIDTH_VAR}, 0px))`,
    py: 1.5,
    borderWidth: 0,
    borderBottomWidth: 1,
    borderStyle: 'solid',
    borderColor: 'separator.divider',
    fontSize: 'xl',
    lineHeight: 'xl',
    fontWeight: 'heavy',
  },
  overline: OVERLINE_STYLES,
  headerActions: {
    position: 'absolute',
    top: 1.5,
    insetEnd: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  closeButton: {
    position: 'absolute',
    top: '0.625rem',
    insetEnd: 2,
    color: 'feature.secondary',
  },
  body: {
    p: 2,
    flex: 1,
  },
  footer: {
    borderWidth: 0,
    borderTop: '1px solid',
    borderTopColor: 'separator.divider',
    px: 2,
    py: 1.5,
  },
} as const satisfies ModalStyleConfigActual['baseStyle'];

const VARIANTS = {
  /**
   * The alertDialog variant is supplied to the Modal component composed by our
   * AlertDialog component, and triggers these styles, which override our modal
   * styles to customize the appearance of our alert dialogs.
   */
  alertDialog: {
    dialogContainer: {
      padding: 0,
    },
    dialog: {
      maxW: '28rem',
    },
    header: {
      paddingTop: 2,
      paddingBottom: 1,
      border: 0,
    },
    body: {
      py: 1,
      fontSize: 'lg',
      lineHeight: 'lg',
      fontWeight: 'default',
    },
    footer: {
      border: 0,
    },
  },
} as const satisfies ModalStyleConfigActual['variants'];

const SIZE_TO_FLEX_BASIS_MAP: Record<
  ModalSize,
  SystemStyleObject['flexBasis']
> = {
  sm: '32rem',
  md: '45rem',
  lg: '58rem',
  xl: '71rem',
  full: '100%',
};

const getBaseSizeStyles = (size: ModalSize): StyleObjectMulti<ModalPart> => ({
  dialog: {
    flexBasis: SIZE_TO_FLEX_BASIS_MAP[size],
    maxWidth: SIZE_TO_FLEX_BASIS_MAP[size],
  },
});

const SIZES = {
  sm: getBaseSizeStyles('sm'),
  md: getBaseSizeStyles('md'),
  lg: getBaseSizeStyles('lg'),
  xl: getBaseSizeStyles('xl'),
  full: () => {
    const base = getBaseSizeStyles('full');

    return {
      ...base,
      dialog: {
        ...base.dialog,
        alignSelf: 'stretch',
        my: 0,
      },
    };
  },
} as const satisfies ModalStyleConfigActual['sizes'];

const DEFAULT_PROPS = {
  size: 'md',
} as const satisfies ModalStyleConfigActual['defaultProps'];

export const MODAL_STYLE_CONFIG: ModalStyleConfigActual = {
  parts: MODAL_PARTS,
  baseStyle: BASE_STYLE,
  variants: VARIANTS,
  sizes: SIZES,
  defaultProps: DEFAULT_PROPS,
};
